import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { API_CONFIG } from "../../common/api";

gsap.registerPlugin(ScrollTrigger);

// ─── Dati placeholder – verranno sostituiti con i contenuti reali ───────────
const CLAIMS_PRODUTTORE = [
  {
    id: "p1",
    icon: "�",
    title: "Nessun costo. Mai.",
    body: "Quota di affiliazione? Non esiste. Canone mensile? No. Il modello è legato alle vendite: Mangitalia trattiene una commissione sul margine; tu ricevi il corrispettivo di produzione concordato. Dettagli in /per-partner.",
  },
  {
    id: "p2",
    icon: "📦",
    title: "Candidatura reale, kit di avvio incluso",
    body: "Spedisci il tuo prodotto esattamente come lo invieresti a un cliente — imballato come si deve, con i materiali scelti da te. Il nostro team lo valuta. Se viene accettato, ricevi nastro da imballo ed etichette personalizzate per re-brandizzare ogni spedizione. Le scatole? Puoi usare le tue o acquistarle direttamente da noi.",
  },
  {
    id: "p3",
    icon: "💸",
    title: "Il tuo compenso arriva alla consegna",
    body: "Il corrispettivo di produzione concordato viene accantonato al pagamento e liberato alla consegna. I PDF di piattaforma sono riepiloghi operativi; la fattura elettronica (SDI) segue il modello definito col commercialista.",
  },
];

const CLAIMS_FORNITORE = [
  {
    id: "f1",
    icon: "🚚",
    title: "Cerchiamo chi sa muovere il cibo sul serio",
    body: "Prodotti freschi che non perdonano i ritardi, secchi da gestire con cura, spedizioni locali il giorno stesso o internazionali con la catena del freddo integra. Se hai la struttura — piccola o grande — per gestire uno o più di questi scenari, vogliamo conoscerti.",
  },
  {
    id: "f2",
    icon: "🔗",
    title: "Costruiamo il digitale insieme",
    body: "Che tu abbia già un gestionale da integrare via API o che stia muovendo i primi passi nella digitalizzazione, c'è spazio per una collaborazione concreta. Non cerchiamo un fornitore: cerchiamo un partner con cui far crescere un ecosistema condiviso.",
  },
  {
    id: "f3",
    icon: "🌍",
    title: "Locale, nazionale, internazionale: scegli la tua scala",
    body: "Non devi coprire tutto il mondo per essere utile. Anche chi gestisce consegne in un singolo distretto può diventare un nodo fondamentale della rete. Ci interessa la qualità del servizio, non solo la copertura geografica.",
  },
];

const SUB_RUOLI_PRODUTTORE = [
  { value: "caseificio", label: "Produttore caseario" },
  { value: "panificatore", label: "Panificatore artigianale" },
  { value: "salumificio", label: "Salumificio / norcineria" },
  { value: "viticoltore", label: "Viticoltore / birraio" },
  { value: "ortofrutta", label: "Ortofrutta & conserve" },
  { value: "altro_prod", label: "Altro produttore" },
];

const SUB_RUOLI_FORNITORE = [
  { value: "logistica_locale", label: "Trasporti locali / last-mile" },
  { value: "logistica_nazionale", label: "Logistica nazionale" },
  { value: "logistica_internazionale", label: "Spedizioni internazionali" },
  { value: "freddo", label: "Trasporto fresco & catena del freddo" },
  { value: "tech_integrazione", label: "Integrazione software / API" },
  { value: "tech_digitale", label: "Digitalizzazione & consulenza tech" },
  { value: "packaging", label: "Packaging & materiali di imballo" },
  { value: "altro_forn", label: "Altro servizio" },
];

// ─── Claims specifici per sotto-ruolo ────────────────────────────────────────
const CLAIMS_SPECIFICI = {
  default: [
    {
      id: "s1",
      icon: "🍎",
      title: "Fresco, secco, stagionale: c'è spazio per tutto",
      body: "Formaggio appena fatto, olio nuovo, conserve estive, salumi a lunga stagionatura — qualsiasi prodotto alimentare artigianale può essere candidato. In fase di candidatura indichi le specifiche (fresco o secco, temperatura di trasporto, shelf-life, stagionalità) e costruiamo insieme il percorso più adatto al tuo prodotto.",
    },
    {
      id: "s2",
      icon: "🔑",
      title: "La tua area riservata: il controllo è tuo",
      body: "Appena il primo prodotto viene accettato, ricevi le credenziali per il tuo portale personale. Da lì gestisci in autonomia: tempi di consegna, raggio massimo di percorrenza, quantità giornaliera erogabile, preavviso richiesto e finestre di prenotazione per i prodotti stagionali — come l'olio nuovo a novembre. Nessuna telefonata, nessun intermediario.",
    },
  ],
};

// ─── Componente ClaimCard ─────────────────────────────────────────────────────
function ClaimCard({ claim, index, onContact }) {
  const cardRef = useRef(null);

  useEffect(() => {
    const el = cardRef.current;
    if (!el) return;
    gsap.fromTo(
      el,
      { opacity: 0, y: 60 },
      {
        opacity: 1,
        y: 0,
        duration: 0.7,
        ease: "power3.out",
        scrollTrigger: {
          trigger: el,
          start: "top 98%",
          toggleActions: "play none none none",
        },
        delay: index * 0.1,
      }
    );
  }, [index]);

  return (
    <div ref={cardRef} className="relative opacity-0">
      <div className="bg-white rounded-3xl shadow-xl p-8 md:p-10 border border-amber-100 group hover:shadow-2xl transition-shadow duration-500">
        <div className="text-5xl mb-4">{claim.icon}</div>
        <h3 className="text-2xl font-bold text-[#c78244] mb-3">{claim.title}</h3>
        <p className="text-gray-600 text-lg leading-relaxed">{claim.body}</p>
      </div>

      {/* Mini CTA contatto dopo ogni claim */}
      <motion.div
        className="mt-3 text-center"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ delay: 0.4 }}
      >
        <button
          onClick={onContact}
          className="text-sm text-[#c78244]/70 hover:text-[#c78244] underline underline-offset-4 transition-colors"
        >
          Hai domande? Contattaci ora →
        </button>
      </motion.div>
    </div>
  );
}

// ─── Componente ContactForm ───────────────────────────────────────────────────
function ContactForm({ ruolo, subRuolo }) {
  const [form, setForm] = useState({ nome: "", email: "", telefono: "", messaggio: "" });
  const [errors, setErrors] = useState({});
  const [sent, setSent] = useState(false);
  const [sendError, setSendError] = useState(false);
  const formRef = useRef(null);

  useEffect(() => {
    const el = formRef.current;
    if (!el) return;
    gsap.fromTo(
      el,
      { opacity: 0, scale: 0.95 },
      {
        opacity: 1,
        scale: 1,
        duration: 0.8,
        ease: "back.out(1.2)",
        scrollTrigger: {
          trigger: el,
          start: "top 98%",
          toggleActions: "play none none none",
        },
      }
    );
  }, []);

  const validate = () => {
    const e = {};
    if (!form.nome.trim()) e.nome = "Il nome è obbligatorio";
    if (!form.email.trim()) {
      e.email = "L'email è obbligatoria";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      e.email = "Email non valida";
    }
    if (form.telefono.trim() && !/^[\d\s\+\-\.\(\)]{6,20}$/.test(form.telefono.trim())) {
      e.telefono = "Numero di telefono non valido";
    }
    if (!form.messaggio.trim()) e.messaggio = "Il messaggio è obbligatorio";
    return e;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }

try {
      const response = await fetch(`${API_CONFIG.BASE_URL}/api/richieste-contatto`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({
          nome: form.nome,
          email: form.email,
          telefono: form.telefono || null,
          messaggio: form.messaggio,
          ruolo: ruolo || null,
          sub_ruolo: subRuolo || null,
          sorgente: 'presentazione',
        }),
      });
      if (!response.ok) {
        throw new Error('Errore server');
      }
      setSent(true);
    } catch (_) {
      setSendError(true);
    }
  };

  if (sent) {
    return (
      <motion.div
        className="text-center py-12"
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ type: "spring", stiffness: 200 }}
      >
        <div className="text-7xl mb-6">🎉</div>
        <h4 className="text-2xl font-bold text-[#c78244] mb-3">Grazie, ti ricontatteremo presto!</h4>
        <p className="text-gray-500">Il tuo messaggio è stato ricevuto. Il nostro team ti scriverà entro 24 ore.</p>
      </motion.div>
    );
  }

  return (
    <div ref={formRef} className="opacity-0">
      <h3 className="text-3xl font-bold text-[#c78244] mb-2 text-center">Mettiti in contatto</h3>
      <p className="text-gray-500 text-center mb-8">
        Sei un <strong className="text-[#c78244]">{ruolo === "produttore" ? "produttore artigianale" : "fornitore di servizi"}</strong>.
        {subRuolo && <> Specializzazione: <strong className="text-[#c78244]">{subRuolo}</strong>.</>} Parliamone.
      </p>

      <form onSubmit={handleSubmit} noValidate className="space-y-5">
        <div className="flex flex-wrap gap-3 justify-center pb-2">
          <Link
            to={`/candidatura?ruolo=${ruolo || 'produttore'}${subRuolo ? `&sub_ruolo=${encodeURIComponent(subRuolo)}` : ''}`}
            className="bg-gradient-to-r from-[#F6CF48] to-[#c78244] text-white font-bold px-6 py-3 rounded-xl shadow"
          >
            Candidati ora — inizia in 2 minuti
          </Link>
        </div>
        <p className="text-center text-xs text-gray-400">oppure compila il form per essere ricontattato</p>
        {sendError && (
          <div className="bg-red-50 border border-red-200 rounded-xl px-5 py-3 text-red-600 text-sm">
            Si è verificato un errore durante l'invio. Riprova tra qualche istante.
          </div>
        )}
        <div>
          <input
            type="text"
            placeholder="Il tuo nome"
            value={form.nome}
            onChange={(e) => { setForm({ ...form, nome: e.target.value }); setErrors({ ...errors, nome: "" }); }}
            className={`w-full rounded-xl border px-5 py-3 text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#c78244] transition ${errors.nome ? "border-red-400" : "border-gray-200"}`}
          />
          {errors.nome && <p className="text-red-500 text-sm mt-1">{errors.nome}</p>}
        </div>

        <div>
          <input
            type="tel"
            placeholder="Numero di telefono (opzionale)"
            value={form.telefono}
            onChange={(e) => { setForm({ ...form, telefono: e.target.value }); setErrors({ ...errors, telefono: "" }); }}
            className={`w-full rounded-xl border px-5 py-3 text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#c78244] transition ${errors.telefono ? "border-red-400" : "border-gray-200"}`}
          />
          {errors.telefono && <p className="text-red-500 text-sm mt-1">{errors.telefono}</p>}
        </div>

        <div>
          <input
            type="email"
            placeholder="La tua email"
            value={form.email}
            onChange={(e) => { setForm({ ...form, email: e.target.value }); setErrors({ ...errors, email: "" }); }}
            className={`w-full rounded-xl border px-5 py-3 text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#c78244] transition ${errors.email ? "border-red-400" : "border-gray-200"}`}
          />
          {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
        </div>

        <div>
          <textarea
            rows={4}
            placeholder="Raccontaci qualcosa di te e del tuo progetto…"
            value={form.messaggio}
            onChange={(e) => { setForm({ ...form, messaggio: e.target.value }); setErrors({ ...errors, messaggio: "" }); }}
            className={`w-full rounded-xl border px-5 py-3 text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#c78244] transition resize-none ${errors.messaggio ? "border-red-400" : "border-gray-200"}`}
          />
          {errors.messaggio && <p className="text-red-500 text-sm mt-1">{errors.messaggio}</p>}
        </div>

        <motion.button
          type="submit"
          whileHover={{ scale: 1.03 }}
          whileTap={{ scale: 0.97 }}
          className="w-full bg-gradient-to-r from-[#F6CF48] to-[#c78244] text-white font-bold py-4 rounded-2xl text-lg shadow-lg hover:shadow-xl transition-shadow"
        >
          Invia il messaggio
        </motion.button>
      </form>
    </div>
  );
}

// ─── Step: selezione sotto-ruolo ──────────────────────────────────────────────
function SubRuoloSelector({ ruolo, onSelect }) {
  const items = ruolo === "produttore" ? SUB_RUOLI_PRODUTTORE : SUB_RUOLI_FORNITORE;
  const containerRef = useRef(null);

  useEffect(() => {
    const els = containerRef.current?.querySelectorAll(".sub-card");
    if (!els) return;
    gsap.fromTo(
      els,
      { opacity: 0, y: 30 },
      {
        opacity: 1,
        y: 0,
        stagger: 0.08,
        duration: 0.5,
        ease: "power2.out",
      }
    );
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className="w-full"
    >
      <h3 className="text-2xl md:text-3xl font-bold text-[#c78244] text-center mb-3">
        Dimmi di più su di te
      </h3>
      <p className="text-gray-500 text-center mb-8">
        Seleziona la tua specializzazione per scoprire come Mangitalia può aiutarti nello specifico.
      </p>

      <div ref={containerRef} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((item) => (
          <motion.button
            key={item.value}
            whileHover={{ scale: 1.04, y: -4 }}
            whileTap={{ scale: 0.97 }}
            onClick={() => onSelect(item)}
            className="sub-card opacity-0 bg-white rounded-2xl border-2 border-amber-100 hover:border-[#c78244] shadow-md hover:shadow-xl px-6 py-5 text-left transition-all duration-300 group"
          >
            <span className="block text-[#c78244] font-bold text-lg group-hover:translate-x-1 transition-transform">
              {item.label}
            </span>
            <span className="block text-gray-400 text-sm mt-1">Scopri il percorso →</span>
          </motion.button>
        ))}
      </div>
    </motion.div>
  );
}

// ─── PAGINA PRINCIPALE ────────────────────────────────────────────────────────
export default function Presentazione() {
  const navigate = useNavigate();

  // step: "intro" | "claims" | "sub-ruolo" | "claims-specifici" | "contact"
  const [step, setStep] = useState("intro");
  const [ruolo, setRuolo] = useState(null); // "produttore" | "fornitore"
  const [subRuolo, setSubRuolo] = useState(null);
  const [hoveredRuolo, setHoveredRuolo] = useState(null);
  const [contactOpen, setContactOpen] = useState(false);

  const heroRef = useRef(null);
  const ruoloBtnRef = useRef({ produttore: null, fornitore: null });
  const pageRef = useRef(null);

  // Label tooltip
  const tooltipMap = {
    produttore: "Artigiano locale",
    fornitore: "Fornitore di servizi",
  };

  // Animazione entrata pagina
  useEffect(() => {
    gsap.fromTo(
      pageRef.current,
      { opacity: 0 },
      { opacity: 1, duration: 0.5, ease: "power2.out" }
    );
  }, []);

  // Quando viene selezionato un ruolo, anima i pulsanti verso l'alto
  const handleSelectRuolo = (selectedRuolo) => {
    setRuolo(selectedRuolo);
    gsap.to(heroRef.current, {
      y: -20,
      duration: 0.5,
      ease: "power2.inOut",
      onComplete: () => setStep("claims"),
    });
  };

  const handleSubRuoloSelect = (item) => {
    setSubRuolo(item.label);
    setStep("claims-specifici");
  };

  const handleContactOpen = () => {
    setContactOpen(true);
    setTimeout(() => {
      document.getElementById("contact-section")?.scrollIntoView({ behavior: "smooth" });
    }, 100);
  };

  const claims = ruolo === "produttore" ? CLAIMS_PRODUTTORE : CLAIMS_FORNITORE;
  const claimsSpecifici = CLAIMS_SPECIFICI.default;

  return (
    <div ref={pageRef} className="min-h-screen bg-[#FFFEFA] opacity-0">

      {/* ── Navbar minimale ───────────────────────────────────────────── */}
      <nav className="sticky top-0 z-50 bg-[#FFFEFA]/90 backdrop-blur-md border-b border-amber-100 px-6 py-3 flex items-center justify-between">
        <button
          onClick={() => navigate("/")}
          className="text-[#c78244] font-semibold hover:opacity-70 transition-opacity flex items-center gap-2"
        >
          ← Torna alla home
        </button>
        <span className="font-sacramento text-2xl text-[#c78244]">Mangitalia</span>
        <button
          onClick={handleContactOpen}
          className="bg-gradient-to-r from-[#F6CF48] to-[#c78244] text-white px-4 py-1.5 rounded-full text-sm font-semibold shadow hover:shadow-md transition-shadow"
        >
          Contattaci
        </button>
      </nav>

      {/* ── Hero / Selezione ruolo ─────────────────────────────────────── */}
      <div ref={heroRef} className="relative overflow-hidden">

        {/* Sfondo decorativo */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div className="absolute -top-32 -left-32 w-96 h-96 rounded-full bg-amber-100/40 blur-3xl" />
          <div className="absolute -bottom-20 -right-20 w-80 h-80 rounded-full bg-yellow-100/50 blur-3xl" />
        </div>

        <div className="relative max-w-5xl mx-auto px-6 pt-20 pb-16 text-center">
          <AnimatePresence mode="wait">
            {step === "intro" && (
              <motion.div
                key="intro"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.6 }}
              >
                <p className="text-sm font-semibold tracking-widest uppercase text-[#c78244]/60 mb-4">
                  Il progetto
                </p>
                <h1 className="text-5xl md:text-7xl font-black text-gray-900 leading-tight mb-6">
                  Sei pronto a fare<br />
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#F6CF48] to-[#c78244]">
                    la differenza?
                  </span>
                </h1>
                <p className="text-xl text-gray-500 max-w-2xl mx-auto mb-14">
                  Mangitalia connette le eccellenze artigianali italiane con chi le ama davvero.
                  Seleziona il tuo ruolo per scoprire come possiamo crescere insieme.
                </p>

                {/* I due pulsanti principali */}
                <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
                  {["produttore", "fornitore"].map((r) => (
                    <div key={r} className="relative">
                      {/* Tooltip */}
                      <AnimatePresence>
                        {hoveredRuolo === r && (
                          <motion.div
                            initial={{ opacity: 0, y: 8, scale: 0.9 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, y: 8, scale: 0.9 }}
                            transition={{ duration: 0.2 }}
                            className="absolute -top-12 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-sm px-4 py-2 rounded-xl whitespace-nowrap shadow-xl z-10"
                          >
                            {tooltipMap[r]}
                            <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-full w-0 h-0 border-x-4 border-x-transparent border-t-4 border-t-gray-900" />
                          </motion.div>
                        )}
                      </AnimatePresence>

                      <motion.button
                        ref={(el) => (ruoloBtnRef.current[r] = el)}
                        onHoverStart={() => setHoveredRuolo(r)}
                        onHoverEnd={() => setHoveredRuolo(null)}
                        onClick={() => handleSelectRuolo(r)}
                        whileHover={{ scale: 1.06, y: -4 }}
                        whileTap={{ scale: 0.97 }}
                        className={`relative px-12 py-6 rounded-3xl font-black text-xl shadow-xl transition-all duration-300 ${
                          r === "produttore"
                            ? "bg-gradient-to-br from-[#F6CF48] to-[#e8a84a] text-white"
                            : "bg-gradient-to-br from-[#c78244] to-[#9a5f2f] text-white"
                        }`}
                      >
                        <span className="capitalize">{r}</span>
                      </motion.button>
                    </div>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* ── Chip ruolo selezionato (appare dopo la scelta) ────────────── */}
      <AnimatePresence>
        {ruolo && step !== "intro" && (
          <motion.div
            key="chip"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex justify-center mb-2"
          >
            <span className="bg-gradient-to-r from-[#F6CF48] to-[#c78244] text-white text-sm font-bold px-6 py-2 rounded-full shadow capitalize">
              Percorso: {ruolo} {subRuolo ? `› ${subRuolo}` : ""}
            </span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Sezione Claims ────────────────────────────────────────────── */}
      <AnimatePresence>
        {(step === "claims" || step === "sub-ruolo" || step === "claims-specifici") && (
          <motion.section
            key="claims"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
            className="max-w-3xl mx-auto px-6 py-12 space-y-8"
          >
            {claims.map((claim, i) => (
              <ClaimCard key={claim.id} claim={claim} index={i} onContact={handleContactOpen} />
            ))}

            {/* CTA per procedere alla selezione sotto-ruolo */}
            {step === "claims" && (
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="pt-8 text-center"
              >
                <p className="text-gray-400 mb-4 text-lg">Vuoi andare ancora più a fondo?</p>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.97 }}
                  onClick={() => setStep("sub-ruolo")}
                  className="bg-white border-2 border-[#c78244] text-[#c78244] font-bold px-10 py-4 rounded-2xl text-lg shadow hover:shadow-lg transition-shadow"
                >
                  Sì, dimmi di più →
                </motion.button>
              </motion.div>
            )}
          </motion.section>
        )}
      </AnimatePresence>

      {/* ── Selezione sotto-ruolo ─────────────────────────────────────── */}
      <AnimatePresence>
        {step === "sub-ruolo" && (
          <motion.section
            key="sub-ruolo"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="max-w-4xl mx-auto px-6 pb-16"
          >
            <SubRuoloSelector ruolo={ruolo} onSelect={handleSubRuoloSelect} />
          </motion.section>
        )}
      </AnimatePresence>

      {/* ── Claims specifici per sotto-ruolo ─────────────────────────── */}
      <AnimatePresence>
        {step === "claims-specifici" && (
          <motion.section
            key="claims-specifici"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
            className="max-w-3xl mx-auto px-6 pb-10 space-y-8"
          >
            <motion.div
              className="text-center py-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <h2 className="text-3xl font-bold text-gray-800 mb-2">
                Proprio quello che fa per te
              </h2>
              <p className="text-gray-400">
                Ecco cosa abbiamo pensato per un <span className="font-semibold text-[#c78244]">{subRuolo}</span>
              </p>
            </motion.div>

            {claimsSpecifici.map((claim, i) => (
              <ClaimCard key={claim.id} claim={claim} index={i} onContact={handleContactOpen} />
            ))}

            {/* Apertura del form finale */}
            {!contactOpen && (
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="pt-6 text-center"
              >
                <p className="text-gray-400 mb-4 text-lg">Pronto per candidarti?</p>
                <div className="flex flex-wrap justify-center gap-3">
                  <Link
                    to={`/candidatura?ruolo=${ruolo || 'produttore'}`}
                    className="bg-gradient-to-r from-[#F6CF48] to-[#c78244] text-white font-bold px-10 py-4 rounded-2xl text-lg shadow-xl"
                  >
                    Candidati ora
                  </Link>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.97 }}
                    onClick={handleContactOpen}
                    className="border-2 border-[#c78244] text-[#c78244] font-bold px-10 py-4 rounded-2xl text-lg"
                  >
                    Contattaci
                  </motion.button>
                </div>
              </motion.div>
            )}
          </motion.section>
        )}
      </AnimatePresence>

      {/* ── Form di contatto (presente sempre quando aperto) ─────────── */}
      <AnimatePresence>
        {contactOpen && (
          <motion.section
            id="contact-section"
            key="contact"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: "easeOut" }}
            className="max-w-2xl mx-auto px-6 py-16"
          >
            <div className="bg-white rounded-3xl shadow-2xl border border-amber-100 p-8 md:p-12">
              <ContactForm ruolo={ruolo} subRuolo={subRuolo} />
            </div>
          </motion.section>
        )}
      </AnimatePresence>

      {/* ── Form di contatto visibile sempre in fondo (per le sezioni claims) ── */}
      <AnimatePresence>
        {(step === "claims" || step === "sub-ruolo") && (
          <motion.section
            key="contact-always"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true, margin: "0px" }}
            transition={{ duration: 0.7 }}
            className="max-w-2xl mx-auto px-6 pb-24"
          >
            <div className="bg-white rounded-3xl shadow-2xl border border-amber-100 p-8 md:p-12">
              <ContactForm ruolo={ruolo} subRuolo={subRuolo} />
            </div>
          </motion.section>
        )}
      </AnimatePresence>

      {/* ── Footer minimale ───────────────────────────────────────────── */}
      <footer className="text-center py-8 text-gray-400 text-sm border-t border-amber-50">
        <div className="flex justify-center gap-4 mb-2">
          <a href="/" className="hover:opacity-80 transition">Home</a>
          <a href="/diventa-closer" className="hover:opacity-80 transition">Programma Inviti</a>
        </div>
        © {new Date().getFullYear()} Mangitalia · Tutti i diritti riservati
      </footer>

    </div>
  );
}
